import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

import logo from "../../../assets/images/wa-horizontal-color.png";
import "./firma.css";

/*
	Firma del contrato desde el celular (/firma/<token>, 2026-10-15): el enlace que le llega al cliente por WhatsApp cuando
	su matrícula queda finalizada. Lee su contrato con letra normal (la versión HTML del mismo contrato; el PDF queda a un
	toque), pone su nombre, firma con el dedo o acepta escribiendo su nombre, y se toma una foto que va en el cuadro
	FOTO / HUELLA. Si el alumno es menor, firma su madre, padre o tutor. Honduras de usted. Sin sesión de WAPP.
*/

const API = (process.env.REACT_APP_API_URL_V2 || "") + "/contrato-firma/";
const WORKER = "/vendor/pdfjs-3.11.174/pdf.worker.min.js";

interface Datos {
	estado: "pendiente" | "firmado"; alumno: string | null; alumnoCorto: string | null; curso: string | null; plantel: string | null;
	fecha: string | null; menor: boolean; usted: boolean; tutorNombre: string | null; firmadoEl: string | null; firmante: string | null;
	huella: string | null; html: string | null;
}

const mensaje = (e: any, porDefecto: string): string => e?.response?.data?.message || porDefecto;

/** Textos de tú (México) y de usted (Honduras). */
const textos = (u: boolean) => ({
	titulo: u ? "Firme Su Contrato" : "Firma Tu Contrato",
	tutor: (a: string) => (u ? "Usted firma" : "Firmas") + " como madre, padre o tutor de " + a + ".",
	nombre: "Nombre completo de quien firma",
	conDedo: "Firmar con el Dedo",
	foto: u ? "Tómese una Foto" : "Tómate una Foto",
	fotoNota: "Va en el cuadro de foto del contrato.",
	repetir: "Repetir Foto",
	acepto: "Leí el contrato y acepto sus términos.",
	firmar: "Firmar Contrato",
	faltaNombre: "Falta el nombre completo (nombre y apellido).",
	faltaFirma: "Falta la firma en el recuadro.",
	faltaFoto: "Falta la foto para el contrato.",
	faltaAcepto: "Falta marcar que leyó y acepta el contrato.",
	errorFoto: "No se pudo usar esa foto. Intente con otra.",
});

/** Las páginas del PDF como imágenes (pdf.js, cargado solo aquí): para contratos sin versión de lectura o si piden el PDF. */
const Paginas: React.FC<{ url: string; onFallo: () => void }> = ({ url, onFallo }) => {
	const caja = useRef<HTMLDivElement>(null);
	const [cargando, setCargando] = useState(true);
	useEffect(() => {
		let vivo = true;
		(async () => {
			try {
				const pdfjs: any = await import("pdfjs-dist/legacy/build/pdf");
				pdfjs.GlobalWorkerOptions.workerSrc = WORKER;
				const doc = await pdfjs.getDocument({ url, withCredentials: false }).promise;
				const ancho = Math.min(caja.current?.clientWidth || 360, 900);
				const ratio = Math.min(window.devicePixelRatio || 1, 2);
				for (let i = 1; i <= doc.numPages && vivo; i++) {
					const pagina = await doc.getPage(i);
					const base = pagina.getViewport({ scale: 1 });
					const vista = pagina.getViewport({ scale: (ancho / base.width) * ratio });
					const canvas = document.createElement("canvas");
					canvas.width = vista.width;
					canvas.height = vista.height;
					canvas.className = "fmPagina";
					canvas.setAttribute("aria-label", "Página " + i + " de " + doc.numPages);
					await pagina.render({ canvasContext: canvas.getContext("2d"), viewport: vista }).promise;
					if (!vivo) return;
					caja.current?.appendChild(canvas);
					if (i === 1) setCargando(false);
				}
			} catch (e) {
				if (vivo) { setCargando(false); onFallo(); }
			}
		})();
		return () => { vivo = false; };
	}, [url, onFallo]);
	return (
		<div className="fmVisor" ref={caja}>
			{cargando ? <div className="fmCargando">Cargando el contrato…</div> : null}
		</div>
	);
};

/** Recuadro para firmar con el dedo (o el mouse). onCambio(true) cuando ya hay una firma de verdad (60 px de trazo, no un punto). */
const Lienzo: React.FC<{ lienzo: React.RefObject<HTMLCanvasElement>; onCambio: (hay: boolean) => void }> = ({ lienzo, onCambio }) => {
	const trazo = useRef<{ x: number; y: number } | null>(null);
	const tinta = useRef(0);
	const preparar = useCallback(() => {
		const c = lienzo.current;
		if (!c) return;
		const r = c.getBoundingClientRect();
		const ratio = window.devicePixelRatio || 1;
		c.width = Math.round(r.width * ratio);
		c.height = Math.round(r.height * ratio);
		const ctx = c.getContext("2d")!;
		ctx.scale(ratio, ratio);
		ctx.lineWidth = 2.4;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";
		ctx.strokeStyle = "#1d1a5e";
		tinta.current = 0;
		onCambio(false);
	}, [lienzo, onCambio]);
	useEffect(() => { preparar(); }, [preparar]);
	const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
		const r = e.currentTarget.getBoundingClientRect();
		return { x: e.clientX - r.left, y: e.clientY - r.top };
	};
	const bajar = (e: React.PointerEvent<HTMLCanvasElement>) => {
		e.currentTarget.setPointerCapture(e.pointerId);
		trazo.current = pos(e);
	};
	const mover = (e: React.PointerEvent<HTMLCanvasElement>) => {
		if (!trazo.current) return;
		const ctx = e.currentTarget.getContext("2d")!;
		const p = pos(e);
		ctx.beginPath();
		ctx.moveTo(trazo.current.x, trazo.current.y);
		ctx.lineTo(p.x, p.y);
		ctx.stroke();
		const antes = tinta.current;
		tinta.current += Math.hypot(p.x - trazo.current.x, p.y - trazo.current.y);
		trazo.current = p;
		if (antes < 60 && tinta.current >= 60) onCambio(true);
	};
	const subir = () => { trazo.current = null; };
	return (
		<div className="fmLienzoCaja">
			<canvas ref={lienzo} className="fmLienzo" onPointerDown={bajar} onPointerMove={mover} onPointerUp={subir} onPointerCancel={subir}
				aria-label="Recuadro para firmar con el dedo" />
			<span className="fmLienzoLinea" aria-hidden="true">Firma</span>
			<button type="button" className="fmBorrar" onClick={preparar}>Borrar</button>
		</div>
	);
};

/** La foto del celular, recortada a 240×320 (como el cuadro del contrato) y en JPEG liviano. */
const aSelfie = (archivo: File): Promise<string> => new Promise((resolve, reject) => {
	const url = URL.createObjectURL(archivo);
	const img = new Image();
	img.onload = () => {
		const W = 240, H = 320;
		const c = document.createElement("canvas");
		c.width = W;
		c.height = H;
		const ctx = c.getContext("2d")!;
		const k = Math.max(W / img.width, H / img.height);
		const w = img.width * k, h = img.height * k;
		ctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
		URL.revokeObjectURL(url);
		resolve(c.toDataURL("image/jpeg", 0.82));
	};
	img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("foto")); };
	img.src = url;
});

const Firma: React.FC<{ token: string }> = ({ token }) => {
	const [datos, setDatos] = useState<Datos | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [verPdf, setVerPdf] = useState(false);
	const [sinVisor, setSinVisor] = useState(false);
	const [nombre, setNombre] = useState("");
	const metodo = "firma";
	const [hayFirma, setHayFirma] = useState(false);
	const [selfie, setSelfie] = useState<string | null>(null);
	const [acepto, setAcepto] = useState(false);
	const [aviso, setAviso] = useState<string | null>(null);
	const [enviando, setEnviando] = useState(false);
	const lienzo = useRef<HTMLCanvasElement>(null);
	const camara = useRef<HTMLInputElement>(null);
	const pdf = API + token + "/pdf";

	useEffect(() => {
		document.title = "Contrato · Washington Academy";
		axios.get(API + token).then((r) => {
			setDatos(r.data);
			setNombre((r.data.menor ? r.data.tutorNombre : r.data.alumno) || "");
		}).catch((e) => setError(mensaje(e, "No se pudo abrir el contrato. Revise su conexión e intente otra vez.")));
	}, [token]);

	const sinVisorCb = useCallback(() => setSinVisor(true), []);
	const t = textos(!!datos?.usted);

	const tomarFoto = async (archivo: File | undefined) => {
		if (!archivo) return;
		try { setSelfie(await aSelfie(archivo)); setAviso(null); }
		catch (e) { setAviso(t.errorFoto); }
	};

	const firmar = async () => {
		setAviso(null);
		const n = nombre.trim().replace(/\s+/g, " ");
		if (n.split(" ").length < 2) return setAviso(t.faltaNombre);
		if (!hayFirma) return setAviso(t.faltaFirma);
		if (!selfie) return setAviso(t.faltaFoto);
		if (!acepto) return setAviso(t.faltaAcepto);
		setEnviando(true);
		try {
			const r = await axios.post(API + token, { nombre: n, metodo, acepto: true, huella: datos?.huella, selfie,
				firma: lienzo.current?.toDataURL("image/png") });
			setDatos((d) => d ? { ...d, estado: "firmado", firmadoEl: r.data.firmadoEl, firmante: n } : d);
			document.querySelector(".fm")?.scrollTo({ top: 0, behavior: "smooth" });
		} catch (e) {
			setAviso(mensaje(e, "No se pudo guardar la firma. Intente otra vez."));
		} finally {
			setEnviando(false);
		}
	};

	const cabeza = (
		<header className="fmCabeza">
			<img src={logo} alt="Washington Academy" className="fmLogo" />
		</header>
	);

	if (error) {
		return <div className="fm">{cabeza}<div className="fmCuerpo"><h1 className="fmTitulo">Enlace No Disponible</h1><p className="fmBajada">{error}</p></div></div>;
	}
	if (!datos) {
		return <div className="fm">{cabeza}<div className="fmCuerpo"><div className="fmCargando">Cargando…</div></div></div>;
	}
	const resumen = [datos.curso, datos.plantel].filter(Boolean).join(" · ");

	if (datos.estado === "firmado") {
		return (
			<div className="fm">{cabeza}
				<div className="fmCuerpo fmEntra fmCentro">
					<div className="fmListo" aria-hidden="true">✓</div>
					<h1 className="fmTitulo">Contrato Firmado</h1>
					<p className="fmBajada">{datos.firmante ? datos.firmante + " · " : ""}{datos.firmadoEl}</p>
					{resumen ? <p className="fmDato">{resumen}</p> : null}
					<p className="fmDato">{datos.usted ? "Le llega su copia firmada por WhatsApp." : "Te llega tu copia firmada por WhatsApp."}</p>
					<a className="fmBoton" href={pdf} target="_blank" rel="noopener noreferrer">Descargar Contrato Firmado</a>
				</div>
			</div>
		);
	}

	const lectura = !!datos.html && !verPdf;
	return (
		<div className="fm">{cabeza}
			<div className="fmCuerpo fmEntra">
				<h1 className="fmTitulo">{t.titulo}</h1>
				<p className="fmBajada">{datos.alumno}{resumen ? " · " + resumen : ""}</p>
				{datos.menor ? <p className="fmTutor">{t.tutor(datos.alumnoCorto || datos.alumno || "")}</p> : null}

				{lectura ? (
					<article className="fmLectura" dangerouslySetInnerHTML={{ __html: datos.html || "" }} />
				) : sinVisor ? null : <Paginas url={pdf} onFallo={sinVisorCb} />}
				<div className="fmVistas">
					{datos.html ? <button type="button" className="fmLink" onClick={() => setVerPdf(!verPdf)}>{verPdf ? "Ver en Texto" : "Ver como PDF"}</button> : null}
					<a className="fmLink" href={pdf} target="_blank" rel="noopener noreferrer">Descargar PDF</a>
				</div>

				<section className="fmForma" aria-label="Firmar">
					<label className="fmCampo">
						<span>{t.nombre}</span>
						<input value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="name" maxLength={160} />
					</label>
					{/* Solo con la firma a mano (Esteban, 2026-10-16: se quitó "Aceptar sin Firma"). */}
					<span className="fmEtiqueta">{t.conDedo}</span>
					<Lienzo lienzo={lienzo} onCambio={setHayFirma} />

					<div className="fmFoto">
						{selfie ? <img src={selfie} alt="Foto para el contrato" className="fmFotoVista" /> : null}
						<div className="fmFotoTexto">
							<button type="button" className={"fmBotonFoto" + (selfie ? " hecha" : "")} onClick={() => camara.current?.click()}>
								{selfie ? t.repetir : t.foto}
							</button>
							<small>{t.fotoNota}</small>
						</div>
						<input ref={camara} type="file" accept="image/*" capture="user" hidden onChange={(e) => { tomarFoto(e.target.files?.[0]); e.target.value = ""; }} />
					</div>

					<label className="fmCheck">
						<input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} />
						<span>{t.acepto}</span>
					</label>
					{aviso ? <div className="fmAviso" role="alert">{aviso}</div> : null}
					<button type="button" className="fmBoton" disabled={enviando} onClick={firmar}>{enviando ? "Firmando…" : t.firmar}</button>
				</section>
			</div>
		</div>
	);
};

export default Firma;
